import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import PremiumDarkAtmosphere from './PremiumDarkAtmosphere';

/**
 * PremiumDarkCard — Card premium dark avec dégradé brunâtre, halo radial, Bebas géant
 * Reprend exactement le style de la section "moment de vérité" que le user a kiffée.
 *
 * Props:
 *   - kicker: string  Petit label uppercase orange/rouge en haut (ex: "💸 LÀ OÙ ÇA FUIT")
 *   - kickerColor: 'orange' | 'red' | 'green' | 'amber'  (default 'orange')
 *   - title: ReactNode  Titre Bebas géant
 *   - subtitle: ReactNode  Texte court sous le titre
 *   - children: contenu intérieur
 *   - footer: ReactNode  Optionnel
 *   - className: extra classes
 *   - testid: data-testid du wrapper
 *   - compact: réduit le padding (utile pour mobile)
 */
const KICKER_COLORS = {
  orange: 'text-[#F05A1A]',
  red: 'text-red-400',
  green: 'text-green-400',
  amber: 'text-amber-400',
};

const PremiumDarkCard = ({
  kicker,
  kickerColor = 'orange',
  title,
  subtitle,
  children,
  footer,
  className = '',
  testid,
  compact = false,
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6 }}
      className={`relative overflow-hidden rounded-3xl my-4 ${className}`}
      style={{ background: 'linear-gradient(180deg, #1a0e0e 0%, #2a1414 100%)' }}
      data-testid={testid}
    >
      {/* Premium atmospheric layer — multi-glow + vignette + dots */}
      <PremiumDarkAtmosphere variant="default" dots={!compact} vignette={false} />

      <div className={`relative ${compact ? 'p-5 sm:p-6' : 'p-6 sm:p-8'}`}>
        {kicker && (
          <p className={`text-[10px] sm:text-[11px] font-black uppercase tracking-widest mb-2 ${KICKER_COLORS[kickerColor] || KICKER_COLORS.orange}`}>
            {kicker}
          </p>
        )}
        {title && (
          <h2 className="font-bebas text-3xl sm:text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight mb-3">
            {title}
          </h2>
        )}
        {subtitle && (
          <p className="text-white/65 text-sm sm:text-base leading-relaxed mb-5">
            {subtitle}
          </p>
        )}
        {children}
        {footer && <div className="mt-5">{footer}</div>}
      </div>
    </motion.section>
  );
};

/**
 * MetricTile — petite tuile intérieure pour afficher un chiffre avec status couleur
 */
export const MetricTile = ({ label, value, unit = '', status = 'neutral', detail, testid }) => {
  const statusMap = {
    danger: { bar: 'bg-red-500', text: 'text-red-400', badge: '🔴' },
    warning: { bar: 'bg-amber-500', text: 'text-amber-400', badge: '🟠' },
    ok: { bar: 'bg-green-500', text: 'text-green-400', badge: '🟢' },
    neutral: { bar: 'bg-white/30', text: 'text-white', badge: '⚪' },
  };
  const s = statusMap[status] || statusMap.neutral;

  return (
    <div
      className="rounded-2xl p-4 bg-white/[0.04] border border-white/10 hover:bg-white/[0.06] transition-colors"
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <p className="text-white/55 text-[10px] font-black uppercase tracking-widest leading-tight">
          {label}
        </p>
        <span className="text-[10px]" aria-hidden="true">{s.badge}</span>
      </div>
      <p className={`font-bebas text-4xl sm:text-5xl leading-none tracking-tight tabular-nums ${s.text}`}>
        {value}
        {unit && <span className="text-lg ml-0.5 opacity-70">{unit}</span>}
      </p>
      {detail && (
        <p className="text-white/45 text-[11px] mt-2 leading-snug">{detail}</p>
      )}
      {/* Mini bar visual */}
      <div className="mt-3 h-1 rounded-full bg-white/5 overflow-hidden">
        <div
          className={`h-full rounded-full ${s.bar} transition-all`}
          style={{
            width:
              status === 'danger' ? '85%' :
              status === 'warning' ? '60%' :
              status === 'ok' ? '35%' :
              '50%',
          }}
        />
      </div>
    </div>
  );
};

export default PremiumDarkCard;
